/**
 * Nightly meals — the settlement's one mandatory upkeep.
 *
 * Night falls on a fixed real-time cadence shared by every settlement. At each
 * boundary the town finishes the work already earned, then eats one ration per
 * resident under a single policy. A resident who goes unfed simply does not
 * work the next day; nothing decays and nobody deserts, so an empty pantry is
 * a plateau, never a spiral.
 */
import type { SettlementQualityCounts } from "./model.js";
import type { SettlementStateV1 } from "./production.js";

export const SETTLEMENT_NIGHT_MS = 8 * 3_600_000;

export function settlementNightIndex(tMs: number): number {
  return Math.floor(Math.max(0, tMs) / SETTLEMENT_NIGHT_MS);
}

export const SETTLEMENT_MEAL_POLICIES = ["balanced", "best-available", "conserve-quality"] as const;
export type SettlementMealPolicy = (typeof SETTLEMENT_MEAL_POLICIES)[number];

/** Personal next-day work-speed bonus by meal grade Q1..Q5. Nutrition and
 * happiness are separate: every ration feeds equally, grade is pure morale. */
export const MEAL_GRADE_HAPPINESS_PERMILLE = [0, 30, 60, 100, 150] as const;
export const MEAL_HAPPINESS_CAP_PERMILLE = 200;

export interface SettlementRationDef {
  id: string;
  prepared: boolean;
  /** Kitchen recipe bonus on top of the grade bonus, inside the shared cap. */
  bonusPermille: number;
}

/** Every edible storage good. Raw farm food is always a valid ration; cooking
 * only ever adds happiness, never calories. */
export const SETTLEMENT_RATIONS: readonly SettlementRationDef[] = [
  { id: "wheat", prepared: false, bonusPermille: 0 },
  { id: "bread", prepared: true, bonusPermille: 20 },
  { id: "hearty-meal", prepared: true, bonusPermille: 40 },
] as const;

export function mealHappinessPermille(def: SettlementRationDef, gradeIndex: number): number {
  return Math.min(MEAL_HAPPINESS_CAP_PERMILLE,
    (MEAL_GRADE_HAPPINESS_PERMILLE[gradeIndex] ?? 0) + def.bonusPermille);
}

export function settlementRationCount(state: SettlementStateV1): number {
  let total = 0;
  for (const def of SETTLEMENT_RATIONS) total += Math.max(0, state.storage[def.id] ?? 0);
  return total;
}

/** Same lazy legacy migration as production storage: ungraded goods are Q2. */
function rationCounts(state: SettlementStateV1, id: string): SettlementQualityCounts {
  const existing = state.qualityStorage?.[id];
  if (existing) return existing;
  const counts: SettlementQualityCounts = [0, state.storage[id] ?? 0, 0, 0, 0];
  (state.qualityStorage ??= {})[id] = counts;
  return counts;
}

interface MealRow {
  def: SettlementRationDef;
  grade: number;
}

function mealOrder(policy: SettlementMealPolicy): MealRow[] {
  const rows: MealRow[] = SETTLEMENT_RATIONS.flatMap((def) =>
    [0, 1, 2, 3, 4].map((grade) => ({ def, grade })));
  if (policy === "best-available") {
    return rows.sort((a, b) => mealHappinessPermille(b.def, b.grade) - mealHappinessPermille(a.def, a.grade)
      || Number(b.def.prepared) - Number(a.def.prepared)
      || b.grade - a.grade || a.def.id.localeCompare(b.def.id));
  }
  if (policy === "conserve-quality") {
    return rows.sort((a, b) => a.grade - b.grade
      || Number(a.def.prepared) - Number(b.def.prepared)
      || a.def.id.localeCompare(b.def.id));
  }
  // Balanced: prepared meals first, then ordinary Q2–Q3 raw food, then Q1,
  // touching Q4–Q5 raw food only when needed to keep everyone fed.
  const bucket = (row: MealRow): number => row.def.prepared ? 0
    : row.grade === 1 || row.grade === 2 ? 1 : row.grade === 0 ? 2 : 3;
  return rows.sort((a, b) => bucket(a) - bucket(b) || a.grade - b.grade
    || a.def.id.localeCompare(b.def.id));
}

/** Consume one ration per resident for night `night` in stable resident-id
 * order, recording fed status and personal meal happiness. Mutates `state`;
 * advanceSettlementProduction owns the boundary schedule and calls this once
 * per elapsed night, so canceling and relogging can never reroll a meal. */
export function resolveSettlementNightMeal(state: SettlementStateV1, night: number): void {
  const order = mealOrder(state.mealPolicy ?? "balanced");
  const residents = state.residents.slice().sort((a, b) => a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
  for (const resident of residents) {
    const row = order.find(({ def, grade }) => (rationCounts(state, def.id)[grade] ?? 0) > 0);
    if (!row) {
      // Unfed: fed status goes stale, so this resident sits out the next day.
      // First-timers materialize yesterday as their last fed day; otherwise
      // the missing field would keep counting as fed forever.
      resident.fedForDay ??= night - 1;
      resident.mealHappinessPermille = 0;
      continue;
    }
    rationCounts(state, row.def.id)[row.grade] -= 1;
    const left = (state.storage[row.def.id] ?? 0) - 1;
    if (left > 0) state.storage[row.def.id] = left;
    else delete state.storage[row.def.id];
    resident.fedForDay = night;
    resident.mealHappinessPermille = mealHappinessPermille(row.def, row.grade);
  }
}

export type SettlementFoodWarning = "none" | "three-nights" | "one-night" | "tonight";

export interface SettlementFoodReport {
  rations: number;
  residents: number;
  /** Whole nights the pantry covers at the current population; null with no residents. */
  nights: number | null;
  nextNightAtMs: number;
  /** How many residents will receive a ration at the next boundary. */
  nextNightFed: number;
  warning: SettlementFoodWarning;
}

/** Overview/logout promise: food runway and tomorrow's workforce. The caller
 * should advance the state to `nowMs` first. */
export function settlementFoodReport(state: SettlementStateV1, nowMs: number): SettlementFoodReport {
  const rations = settlementRationCount(state);
  const residents = state.residents.length;
  const nights = residents > 0 ? Math.floor(rations / residents) : null;
  const warning: SettlementFoodWarning = residents === 0 ? "none"
    : rations < residents ? "tonight"
      : nights! <= 1 ? "one-night"
        : nights! <= 3 ? "three-nights" : "none";
  return {
    rations,
    residents,
    nights,
    nextNightAtMs: (settlementNightIndex(Number.isFinite(nowMs) ? nowMs : 0) + 1) * SETTLEMENT_NIGHT_MS,
    nextNightFed: Math.min(residents, rations),
    warning,
  };
}
